/**
 * burtContext.js — Loads live family data from Firestore for Burt's system prompt
 *
 * Provides real family context so Burt can answer:
 * - "What are my chores?" → lists actual pending chores
 * - "How much allowance do I have?" → shows real balance
 * - "Who's in my family?" → lists actual members
 * - "What's on the shopping list?" → shows actual items
 *
 * Uses the actual Firestore collection structure:
 * - tasks: root collection, filtered by familyId
 * - users: root collection, filtered by familyId (parent users)
 * - families/{familyId}/children: child profiles
 * - shoppingList: root collection, filtered by familyId
 * - families/{familyId}: family document
 */

import { collection, getDocs, doc, getDoc, query, where, limit, orderBy } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Load comprehensive family context for Burt's system prompt.
 * @param {string} familyId - The family document ID
 * @param {string} userId - The current user's UID (parent)
 * @returns {Promise<string>} - A formatted context string for Burt's system prompt
 */
export async function loadBurtContext(familyId, userId) {
  if (!familyId || !userId) {
    return 'Family context unavailable (user not authenticated or no family found).';
  }

  const sections = [];

  // 1. Family overview
  try {
    const familyDoc = await getDoc(doc(db, 'families', familyId));
    if (familyDoc.exists()) {
      const fd = familyDoc.data();
      const name = fd.name || fd.familyName || 'This Family';
      const sub = fd.subscriptionStatus || fd.subscription?.status || 'basic';
      sections.push(`FAMILY: "${name}" | Plan: ${sub}`);
    }
  } catch (e) {
    sections.push('Family info: unavailable');
  }

  // 2. Family members (parents from users collection + children from subcollection)
  try {
    const members = [];

    // Parent users
    const usersSnap = await getDocs(
      query(collection(db, 'users'), where('familyId', '==', familyId), limit(10))
    );
    usersSnap.docs.forEach(d => {
      const u = d.data();
      const name = u.displayName || u.firstName || u.email?.split('@')[0] || 'Parent';
      const balance = u.allowanceBalance !== undefined ? ` | Balance: $${(u.allowanceBalance || 0).toFixed(2)}` : '';
      members.push(`  - ${name} (parent${balance})`);
    });

    // Children
    const childrenSnap = await getDocs(
      collection(db, 'families', familyId, 'children')
    );
    childrenSnap.docs.forEach(d => {
      const c = d.data();
      const name = c.firstName || c.displayName || 'Child';
      const age = c.age ? `, age ${c.age}` : '';
      const balance = c.allowanceBalance !== undefined ? ` | Balance: $${(c.allowanceBalance || 0).toFixed(2)}` : '';
      const earned = c.totalEarned !== undefined ? ` | Earned: $${(c.totalEarned || 0).toFixed(2)}` : '';
      members.push(`  - ${name} (child${age}${balance}${earned})`);
    });

    if (members.length > 0) {
      sections.push(`FAMILY MEMBERS (${members.length}):\n${members.join('\n')}`);
    } else {
      sections.push('FAMILY MEMBERS: None added yet.');
    }
  } catch (e) {
    sections.push('Family members: unavailable');
  }

  // 3. Active/pending chores (from root tasks collection)
  try {
    const tasksSnap = await getDocs(
      query(
        collection(db, 'tasks'),
        where('familyId', '==', familyId),
        where('status', 'in', ['pending', 'submitted']),
        limit(20)
      )
    );
    if (!tasksSnap.empty) {
      const tasks = tasksSnap.docs.map(d => {
        const t = d.data();
        const status = t.status === 'submitted' ? '(awaiting approval)' : '(pending)';
        const assignee = t.assignedToName || t.childName || t.assignedTo || 'Unassigned';
        const due = t.dueDate ? ` | Due: ${t.dueDate}` : '';
        const points = t.points !== undefined ? ` | ${t.points} pts` : '';
        const reward = t.reward !== undefined ? ` | $${t.reward}` : '';
        return `  - "${t.title || t.name}" → ${assignee} ${status}${due}${points}${reward}`;
      });
      sections.push(`ACTIVE CHORES (${tasks.length}):\n${tasks.join('\n')}`);
    } else {
      sections.push('ACTIVE CHORES: No pending chores right now. 🎉');
    }
  } catch (e) {
    sections.push('Chores: unavailable');
  }

  // 4. Shopping list
  try {
    const shoppingSnap = await getDocs(
      query(
        collection(db, 'shoppingList'),
        where('familyId', '==', familyId),
        where('completed', '==', false),
        limit(20)
      )
    );
    if (!shoppingSnap.empty) {
      const items = shoppingSnap.docs.map(d => {
        const i = d.data();
        const qty = i.quantity ? ` (${i.quantity})` : '';
        const cat = i.category ? ` [${i.category}]` : '';
        const price = i.estimatedPrice ? ` ~$${i.estimatedPrice}` : '';
        return `  - ${i.name || i.item || 'Item'}${qty}${cat}${price}`;
      });
      sections.push(`SHOPPING LIST (${items.length} items to buy):\n${items.join('\n')}`);
    } else {
      sections.push('SHOPPING LIST: Empty.');
    }
  } catch (e) {
    sections.push('Shopping list: unavailable');
  }

  // 5. Recently completed chores (last 5)
  try {
    const completedSnap = await getDocs(
      query(
        collection(db, 'tasks'),
        where('familyId', '==', familyId),
        where('status', '==', 'approved'),
        orderBy('updatedAt', 'desc'),
        limit(5)
      )
    );
    if (!completedSnap.empty) {
      const completed = completedSnap.docs.map(d => {
        const t = d.data();
        const who = t.assignedToName || t.childName || t.assignedTo || 'Unknown';
        return `  - "${t.title || t.name}" completed by ${who}`;
      });
      sections.push(`RECENTLY COMPLETED CHORES:\n${completed.join('\n')}`);
    }
  } catch (e) {
    // Non-critical, skip silently
  }

  // Build final context string
  return [
    `=== LIVE FAMILY DATA (${new Date().toLocaleString()}) ===`,
    ...sections,
    '=== END FAMILY DATA ===',
    '',
    'Instructions: Use the family data above to give personalized, accurate answers.',
    'If data shows "unavailable", acknowledge you cannot access that info right now.',
    'Always refer to family members by name when possible.',
  ].join('\n');
}

/**
 * Quick context summary using already-loaded props data (no extra Firestore reads).
 * Use this when you already have tasks/members/shoppingList from React context.
 */
export function buildBurtContextFromProps(tasks = [], familyMembers = [], shoppingList = [], familyData = {}) {
  const lines = [];

  const familyName = familyData?.name || familyData?.familyName || 'your family';
  lines.push(`FAMILY: "${familyName}"`);

  if (familyMembers.length > 0) {
    const memberList = familyMembers.map(m => {
      const name = m.displayName || m.firstName || m.name || 'Member';
      const role = m.role || m.type || 'member';
      const balance = m.allowanceBalance !== undefined ? ` ($${(m.allowanceBalance || 0).toFixed(2)})` : '';
      return `  - ${name} (${role}${balance})`;
    }).join('\n');
    lines.push(`FAMILY MEMBERS:\n${memberList}`);
  }

  const pendingTasks = tasks.filter(t => !t.completed && t.status !== 'approved');
  if (pendingTasks.length > 0) {
    const taskList = pendingTasks.slice(0, 10).map(t => {
      const assignee = t.assignedToName || t.childName || 'Unassigned';
      const status = t.submitted ? '(awaiting approval)' : '(pending)';
      return `  - "${t.title || t.name}" → ${assignee} ${status}`;
    }).join('\n');
    lines.push(`ACTIVE CHORES (${pendingTasks.length}):\n${taskList}`);
  } else {
    lines.push('ACTIVE CHORES: None pending.');
  }

  const unpurchased = shoppingList.filter(i => !i.completed && !i.purchased);
  if (unpurchased.length > 0) {
    lines.push(`SHOPPING LIST: ${unpurchased.length} items - ${unpurchased.slice(0, 5).map(i => i.name || i.item).join(', ')}${unpurchased.length > 5 ? '...' : ''}`);
  }

  return lines.join('\n');
}
